import cloneDeep from 'lodash/cloneDeep';

import {
  Assignee,
  ExecutionStatus,
  TestCaseInCycle,
  TestCycle,
  TestExecution,
} from '@qality/common-model';
import { FlagService, TestExecutionService } from '@qality/common-service';

export const handleBulkUpdateExecutionAssignee = async (
  testCasesToUpdate: Array<TestCaseInCycle>,
  newAssignee: Assignee,
  updateTestCycle: (testCycleToUpdate: TestCycle) => void,
  testCycle: TestCycle
): Promise<void> => {
  try {
    const { testCasesWithExecution, testCasesWithoutExecution } =
      divideTestCasesByExecution(testCasesToUpdate);

    const [newExecutions] = await Promise.all([
      bulkCreateTestExecutionsIfNeeded(testCasesWithoutExecution, {
        assignee: newAssignee,
      }),
      bulkUpdateExecutionAssigneeIfNeeded(testCasesWithExecution, newAssignee),
    ]);

    const updatedTestCycle = cloneDeep(testCycle);

    updatedTestCycle.testCases.forEach((testCase) => {
      const matchingTestCaseToUpdate = testCasesToUpdate.find(
        (testCaseToUpdate) => testCaseToUpdate.id === testCase.id
      );

      if (matchingTestCaseToUpdate) {
        const matchingExecution = newExecutions.find(
          (execution) => execution.testCaseId === testCase.testCaseId
        );

        if (matchingExecution) {
          testCase.testExecution = matchingExecution;
        } else {
          testCase.testExecution.executionAssignee = newAssignee.user;
        }
      }
    });

    updateTestCycle(updatedTestCycle);
  } catch (e) {
    const message =
      e.response?.data?.message ||
      'We could not update the assignee in the test executions. Please try again. If the problem persists, feel free to contact our support.';
    FlagService.createErrorFlag('Error', message);
  }
};

export const handleBulkUpdateExecutionStatus = async (
  testCasesToUpdate: Array<TestCaseInCycle>,
  newExecutionStatus: ExecutionStatus,
  updateTestCycle: (testCycleToUpdate: TestCycle) => void,
  testCycle: TestCycle
): Promise<void> => {
  try {
    const { testCasesWithExecution, testCasesWithoutExecution } =
      divideTestCasesByExecution(testCasesToUpdate);

    const [newExecutions] = await Promise.all([
      bulkCreateTestExecutionsIfNeeded(testCasesWithoutExecution, {
        executionStatus: newExecutionStatus,
      }),
      bulkUpdateExecutionStatusIfNeeded(
        testCasesWithExecution,
        newExecutionStatus
      ),
    ]);

    const updatedTestCycle = cloneDeep(testCycle);

    updatedTestCycle.testCases.forEach((testCase) => {
      const matchingTestCaseToUpdate = testCasesToUpdate.find(
        (testCaseToUpdate) => testCaseToUpdate.id === testCase.id
      );

      if (matchingTestCaseToUpdate) {
        const matchingExecution = newExecutions.find(
          (execution) => execution.testCaseId === testCase.testCaseId
        );

        if (matchingExecution) {
          testCase.testExecution = matchingExecution;
        }

        testCase.testExecution.status = newExecutionStatus;
        testCase.statusDetail = newExecutionStatus;
      }
    });

    updateTestCycle(updatedTestCycle);
  } catch (e) {
    const message =
      e.response?.data?.message ||
      'We could not update the status in the test executions. Please try again. If the problem persists, feel free to contact our support.';
    FlagService.createErrorFlag('Error', message);
  }
};

export const divideTestCasesByExecution = (
  testCasesToUpdate: Array<TestCaseInCycle>
): {
  testCasesWithExecution: Array<TestCaseInCycle>;
  testCasesWithoutExecution: Array<TestCaseInCycle>;
} => {
  const testCasesWithExecution = testCasesToUpdate.filter(
    (testCase) => testCase.testExecution !== null
  );
  const testCasesWithoutExecution = testCasesToUpdate.filter(
    (testCase) => testCase.testExecution === null
  );

  return { testCasesWithExecution, testCasesWithoutExecution };
};

const bulkCreateTestExecutionsIfNeeded = async (
  testCasesToUpdate: Array<TestCaseInCycle>,
  fields: {
    assignee?: Assignee;
    executionStatus?: ExecutionStatus;
  }
): Promise<Array<TestExecution>> => {
  if (testCasesToUpdate.length > 0) {
    const newExecutions = await TestExecutionService.bulkCreateTestExecution(
      testCasesToUpdate,
      fields
    );

    newExecutions.forEach((execution) => {
      if (fields.assignee?.user) {
        execution.executionAssignee = fields.assignee.user;
      }

      if (fields.executionStatus) {
        execution.status = fields.executionStatus;
      }
    });

    return newExecutions;
  }

  return [];
};

const bulkUpdateExecutionAssigneeIfNeeded = async (
  testCasesWithExecution: Array<TestCaseInCycle>,
  newAssignee: Assignee
): Promise<void> => {
  if (testCasesWithExecution.length > 0) {
    const testExecutionsBulkData = {
      testExecutions: testCasesWithExecution.map((testCase) => {
        return {
          testExecutionId: testCase.testExecution.id,
          executionAssignee: newAssignee.user?.getIdentifier() ?? null,
        };
      }),
    };

    await TestExecutionService.bulkUpdateExecutionsAssignee(
      testExecutionsBulkData
    );
  }
};

const bulkUpdateExecutionStatusIfNeeded = async (
  testCasesWithExecution: Array<TestCaseInCycle>,
  newExecutionStatus: ExecutionStatus
): Promise<void> => {
  if (testCasesWithExecution.length > 0) {
    const requestData = {
      testExecutions: testCasesWithExecution.map((testCase) => {
        return {
          executionStatus: newExecutionStatus,
          testExecutionId: testCase.testExecution.id,
        };
      }),
    };

    await TestExecutionService.bulkUpdateExecutionsStatus(requestData);
  }
};
